import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { filterNavByPeriod, type NavPeriod } from '@/lib/mutualFundMetrics';
import type { MutualFundNavEntry } from '@/types/mutualFund';

export type { NavPeriod };

interface NavChartProps {
  data: MutualFundNavEntry[];
  period: NavPeriod;
  currency: string;
  baseCurrency: string;
  convertValue: (amount: number) => number;
  isLoading: boolean;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function CustomTooltip({ active, payload, currency }: any) {
  if (!active || !payload?.length) return null;
  const { nav_date, nav_per_unit_converted } = payload[0].payload;
  const date = new Date(nav_date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">{date}</p>
      <p className="text-lg font-semibold">{formatCurrency(nav_per_unit_converted, currency)}</p>
    </div>
  );
}

export function NavChart({ data, period, currency, baseCurrency, convertValue, isLoading }: NavChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>NAV History</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const filtered = filterNavByPeriod(data, period);
  const chartData = filtered.map((e) => ({
    ...e,
    nav_per_unit_converted: convertValue(e.nav_per_unit),
  }));

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>NAV History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No NAV history available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>NAV History</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className="w-full overflow-x-auto"
          role="img"
          aria-label={`NAV history chart showing ${chartData.length} data points in ${currency}`}
        >
          <div className="min-w-[500px]">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="nav_date"
                  tickFormatter={(v) =>
                    new Date(v).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                  }
                  className="text-xs"
                />
                <YAxis
                  tickFormatter={(v) => formatCurrency(v, currency)}
                  className="text-xs"
                />
                <Tooltip content={<CustomTooltip currency={currency} />} />
                <Line
                  type="monotone"
                  dataKey="nav_per_unit_converted"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Screen-reader accessible data table */}
        <div className="sr-only">
          <table>
            <caption>NAV history data</caption>
            <thead>
              <tr>
                <th>Date</th>
                <th>NAV per unit ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    {new Date(entry.nav_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </td>
                  <td>{formatCurrency(entry.nav_per_unit_converted, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
